import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { canAccessManagementReport, clearManagementReportAccess } from '../../utils/managementReportAccess';
import { canAccessComplaints, clearComplaintsAccess } from '../../utils/complaintsAccess';
import { canAccessMonitoring, clearMonitoringAccess } from '../../utils/monitoringAccess';
import { canAccessAmi, clearAmiAccess } from '../../utils/amiAccess';
import { canAccessCallCenterReport, clearCallCenterReportAccess } from '../../utils/callCenterReportAccess';
import { canAccessHisReport, clearHisReportAccess } from '../../utils/hisReportAccess';
import { canAccessCustomerClub, clearCustomerClubAccess, clearCustomerClubSegmentsAccess } from '../../utils/customerClubAccess';
import { clearScreenPopAccess } from '../../utils/screenPopAccess';
import { HIS_HUB_PATH, HIS_MENU_TITLE, HIS_SECTIONS } from '../../config/hisSections';

interface MenuChild {
    title: string;
    path: string;
}

interface MenuItem {
    title: string;
    path: string;
    icon: React.ReactNode;
    adminOnly?: boolean;
    allowedRoles?: string[];
    visible?: () => boolean;
    children?: MenuChild[];
}

interface AdminSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    collapsed: boolean;
    onToggleCollapse: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
    isOpen,
    onClose,
    collapsed,
    onToggleCollapse,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const role = localStorage.getItem('admin_role') || 'user';
    const roleNorm = role.trim().toLowerCase();
    const isPrivileged = roleNorm === 'admin';
    const username = localStorage.getItem('admin_username') || 'مدیر سیستم';

    const handleLogout = async () => {
        try {
            await authAPI.logout();
        } catch {
            // ignore
        } finally {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_username');
            localStorage.removeItem('admin_role');
            clearManagementReportAccess();
            clearComplaintsAccess();
            clearMonitoringAccess();
            clearAmiAccess();
            clearCallCenterReportAccess();
            clearHisReportAccess();
            clearCustomerClubAccess();
            clearCustomerClubSegmentsAccess();
            clearScreenPopAccess();
            navigate('/admin/login');
        }
    };

    const roleLabel = (() => {
        const r = localStorage.getItem('admin_role');
        if (r === 'admin' || r === 'superadmin') return 'مدیر کل';
        if (r === 'manager') return 'مدیر';
        if (r === 'expert') return 'کارشناس';
        return 'کاربر سیستم';
    })();

    const menuItems: MenuItem[] = [
        {
            title: 'داشبورد',
            path: '/admin/dashboard',
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
            ),
        },
        {
            title: 'گزارش',
            path: '/admin/reports',
            visible: () => canAccessManagementReport(),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
            ),
        },
        {
            title: 'جستجوی نوبت‌ها',
            path: '/admin/search',
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            ),
        },
        {
            title: 'انتقادات و پیشنهادات',
            path: '/admin/complaints',
            visible: () => canAccessComplaints(),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            ),
        },
        {
            title: 'نوبت‌های امروز',
            path: '/admin/today-appointments',
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
        },
        {
            title: 'کاربران',
            path: '/admin/users',
            adminOnly: true,
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
        },
        {
            title: 'خدمات',
            path: '/admin/services',
            adminOnly: true,
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
            ),
        },
        {
            title: 'مانیتورینگ',
            path: '/admin/monitoring',
            visible: () => canAccessMonitoring(),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
        },
        {
            title: 'مانیتورینگ ادمین',
            path: '/admin/admin-monitoring',
            adminOnly: true,
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5V4H2v16h5m10 0v-2a4 4 0 00-4-4H11a4 4 0 00-4 4v2m10 0H7m10-12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
        },
        {
            title: 'تنظیمات پیامک',
            path: '/admin/settings/sms',
            adminOnly: true,
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
            ),
        },
        {
            title: 'گزارش مرکز تماس',
            path: '/admin/call-reports',
            visible: () => canAccessCallCenterReport(),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            ),
        },
        {
            title: HIS_MENU_TITLE,
            path: HIS_HUB_PATH,
            visible: () => canAccessHisReport(),
            children: HIS_SECTIONS.map((section) => ({
                title: section.title,
                path: section.path,
            })),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
        },
        {
            title: 'باشگاه مشتریان',
            path: '/admin/customer-club',
            visible: () => canAccessCustomerClub(),
            children: [
                { title: 'سگمنت‌ها', path: '/admin/customer-club' },
                { title: 'گزارش‌ساز', path: '/admin/customer-club/builder' },
                { title: 'دیده‌بان', path: '/admin/customer-club/watchdog' },
                { title: 'نقدی و استرداد', path: '/admin/customer-club/cash' },
            ],
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
        },
        {
            title: 'مرکز تلفن (AMI)',
            path: '/admin/ami',
            visible: () => canAccessAmi(),
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            ),
        },
        {
            title: 'پروفایل من',
            path: '/admin/profile',
            icon: (
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
        },
    ];

    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (location.pathname === HIS_HUB_PATH || location.pathname.startsWith(`${HIS_HUB_PATH}/`)) {
            setExpandedGroups((prev) => ({ ...prev, [HIS_HUB_PATH]: true }));
        }
    }, [location.pathname]);

    const navigateTo = (path: string) => {
        navigate(path);
        if (window.innerWidth < 1024) {
            onClose();
        }
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px] lg:hidden"
                    onClick={onClose}
                    aria-hidden
                />
            )}

            <aside
                className={`
                    fixed top-0 right-0 z-50 flex h-full w-[min(18rem,88vw)] flex-col bg-slate-900 text-white shadow-2xl
                    transition-[width,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                    lg:translate-x-0
                    ${collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'}
                `}
            >
                <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 lg:hidden">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{username}</p>
                        <p className="truncate text-xs text-slate-400">{roleLabel}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                        aria-label="بستن منو"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <button
                    type="button"
                    onClick={onToggleCollapse}
                    title={collapsed ? 'باز کردن منو' : 'جمع کردن منو'}
                    aria-label={collapsed ? 'باز کردن منو' : 'جمع کردن منو'}
                    className="absolute -left-3 top-7 z-40 hidden h-7 w-7 items-center justify-center rounded-full border border-slate-600 bg-slate-800 text-slate-300 shadow-lg transition-all duration-300 hover:scale-110 hover:border-blue-500 hover:bg-blue-600 hover:text-white lg:flex"
                >
                    <svg
                        className={`h-4 w-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>

                <nav className="custom-scrollbar flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-3 pb-2 pt-4 lg:pt-6">
                    <div
                        className={`mb-4 hidden border-b border-slate-800 pb-4 transition-all duration-300 lg:block ${
                            collapsed ? 'px-0' : 'px-1'
                        }`}
                    >
                        <div
                            className={`flex items-center transition-all duration-300 ${
                                collapsed ? 'lg:flex-col lg:gap-2' : 'gap-3'
                            }`}
                        >
                            <div
                                className={`flex shrink-0 items-center justify-center rounded-full bg-blue-500 font-bold text-white shadow-lg shadow-blue-900/30 transition-all duration-300 h-10 w-10 text-lg ${
                                    collapsed ? 'lg:h-9 lg:w-9 lg:text-sm' : ''
                                }`}
                                title={collapsed ? username : undefined}
                            >
                                {username[0].toUpperCase()}
                            </div>

                            <div
                                className={`min-w-0 flex-1 overflow-hidden transition-all duration-300 max-h-20 opacity-100 ${
                                    collapsed ? 'lg:max-h-0 lg:max-w-0 lg:opacity-0' : ''
                                }`}
                            >
                                <p className="truncate text-sm font-medium text-white">{username}</p>
                                <p className="truncate text-xs text-slate-400">{roleLabel}</p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1">
                    {menuItems.map((item, index) => {
                        if (item.adminOnly && !isPrivileged) return null;
                        if (item.visible && !item.visible()) return null;
                        if (item.allowedRoles && !item.allowedRoles.includes(roleNorm)) return null;

                        const hasChildren = Boolean(item.children?.length);
                        const isGroupOpen = hasChildren
                            ? (expandedGroups[item.path] ?? location.pathname.startsWith(`${item.path}/`))
                            : false;
                        const isParentActive = location.pathname === item.path;
                        const isGroupActive =
                            isParentActive ||
                            (hasChildren && location.pathname.startsWith(`${item.path}/`));

                        if (hasChildren) {
                            return (
                                <div key={index} className="space-y-0.5">
                                    <div
                                        className={`group relative flex w-full items-center gap-1 rounded-xl px-1 py-1 transition-all duration-300 ${
                                            collapsed ? 'lg:justify-center lg:px-0 lg:py-3' : ''
                                        } ${
                                            isGroupActive
                                                ? 'bg-blue-600/90 text-white shadow-lg shadow-blue-900/40'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            title={collapsed ? item.title : undefined}
                                            onClick={() => {
                                                navigateTo(item.path);
                                                setExpandedGroups((prev) => ({ ...prev, [item.path]: true }));
                                            }}
                                            className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 transition-all duration-300 ${
                                                collapsed ? 'lg:justify-center lg:px-0 lg:py-2' : ''
                                            }`}
                                        >
                                            <span
                                                className={`shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                                                    isGroupActive ? 'text-white' : 'text-slate-500 group-hover:text-white'
                                                }`}
                                            >
                                                {item.icon}
                                            </span>
                                            <span
                                                className={`max-w-[9rem] overflow-hidden whitespace-nowrap text-sm font-medium opacity-100 transition-all duration-300 ${
                                                    collapsed ? 'lg:max-w-0 lg:opacity-0' : ''
                                                }`}
                                            >
                                                {item.title}
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            aria-label={isGroupOpen ? 'بستن زیرمنو' : 'باز کردن زیرمنو'}
                                            onClick={() =>
                                                setExpandedGroups((prev) => ({
                                                    ...prev,
                                                    [item.path]: !isGroupOpen,
                                                }))
                                            }
                                            className={`shrink-0 rounded-lg p-1.5 transition ${
                                                collapsed ? 'lg:hidden' : ''
                                            } ${
                                                isGroupActive
                                                    ? 'text-white/90 hover:bg-blue-500/40'
                                                    : 'text-slate-500 hover:bg-slate-700 hover:text-white'
                                            }`}
                                        >
                                            <svg
                                                className={`h-4 w-4 transition-transform duration-200 ${
                                                    isGroupOpen ? 'rotate-180' : ''
                                                }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {isGroupActive && collapsed && (
                                            <span className="absolute -left-0.5 top-1/2 hidden h-5 w-1 -translate-y-1/2 rounded-r bg-white lg:block" />
                                        )}

                                        {collapsed && (
                                            <span className="pointer-events-none absolute left-full top-1/2 z-50 mr-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-all duration-200 group-hover:opacity-100 lg:block">
                                                {item.title}
                                            </span>
                                        )}
                                    </div>

                                    {isGroupOpen && (
                                        <div
                                            className={`mr-5 space-y-0.5 border-r border-slate-700/80 pr-2 ${
                                                collapsed ? 'lg:hidden' : ''
                                            }`}
                                        >
                                            {item.children!.map((child) => {
                                                const matches = item.children!.filter(
                                                    (c) =>
                                                        location.pathname === c.path ||
                                                        location.pathname.startsWith(`${c.path}/`)
                                                );
                                                const bestMatch = [...matches].sort(
                                                    (a, b) => b.path.length - a.path.length
                                                )[0];
                                                const isChildActive = bestMatch?.path === child.path;

                                                return (
                                                    <button
                                                        key={child.path}
                                                        type="button"
                                                        onClick={() => navigateTo(child.path)}
                                                        className={`flex w-full items-center rounded-lg px-3 py-2 text-right text-sm transition-all duration-200 ${
                                                            isChildActive
                                                                ? 'bg-slate-800 font-medium text-white'
                                                                : 'text-slate-400 hover:bg-slate-800/70 hover:text-white'
                                                        }`}
                                                    >
                                                        <span className="truncate">{child.title}</span>
                                                        {isChildActive && (
                                                            <span className="mr-auto h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const isActive =
                            location.pathname === item.path ||
                            (item.path !== '/admin/dashboard' &&
                                location.pathname.startsWith(`${item.path}/`));

                        return (
                            <button
                                key={index}
                                type="button"
                                title={collapsed ? item.title : undefined}
                                onClick={() => navigateTo(item.path)}
                                className={`
                                    group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 transition-all duration-300
                                    ${collapsed ? 'lg:justify-center lg:px-0 lg:py-3' : ''}
                                    ${
                                        isActive
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }
                                `}
                            >
                                <span
                                    className={`shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                                        isActive ? 'text-white' : 'text-slate-500 group-hover:text-white'
                                    }`}
                                >
                                    {item.icon}
                                </span>

                                <span
                                    className={`max-w-[11rem] overflow-hidden whitespace-nowrap text-sm font-medium opacity-100 transition-all duration-300 ${
                                        collapsed ? 'lg:max-w-0 lg:opacity-0' : ''
                                    }`}
                                >
                                    {item.title}
                                </span>

                                {isActive && (
                                    <div
                                        className={`mr-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-white ${
                                            collapsed ? 'lg:hidden' : ''
                                        }`}
                                    />
                                )}

                                {isActive && collapsed && (
                                    <span className="absolute -left-0.5 top-1/2 hidden h-5 w-1 -translate-y-1/2 rounded-r bg-white lg:block" />
                                )}

                                {collapsed && (
                                    <span className="pointer-events-none absolute left-full top-1/2 z-50 mr-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-all duration-200 group-hover:opacity-100 lg:block">
                                        {item.title}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    </div>
                </nav>

                <div className="shrink-0 border-t border-slate-800 px-3 py-3">
                    <button
                        type="button"
                        title={collapsed ? 'خروج' : undefined}
                        onClick={() => void handleLogout()}
                        className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-slate-400 transition-all duration-300 hover:bg-red-950/40 hover:text-red-400 ${
                            collapsed ? 'lg:justify-center lg:px-0 lg:py-3' : ''
                        }`}
                    >
                        <span className="shrink-0 transition-transform duration-300 group-hover:scale-110">
                            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </span>
                        <span
                            className={`max-w-[11rem] overflow-hidden whitespace-nowrap text-sm font-medium opacity-100 transition-all duration-300 ${
                                collapsed ? 'lg:max-w-0 lg:opacity-0' : ''
                            }`}
                        >
                            خروج
                        </span>
                        {collapsed && (
                            <span className="pointer-events-none absolute left-full top-1/2 z-50 mr-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-xl transition-all duration-200 group-hover:opacity-100 lg:block">
                                خروج
                            </span>
                        )}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default AdminSidebar;
